import { useMemo, useState } from "react";

import { Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import CustomBasicModal from "app/pages/sales-intelligence/common-components/modals/CustomBasicModal/CustomBasicModal";

import {
    modalStyles,
    StyledButtonsContainer,
    StyledCounter,
    StyledHeader,
    StyledPositionRelative,
    StyledText,
    StyledTextarea,
    StyledTextareaContainer,
} from "./styles";

type BulkAddModalProps = {
    onAddValues: (parsedValue: string[]) => void;
    onOpen?: () => void;
    onClose?: () => void;
    parseInputValue: (value: string) => string[];
    header: string;
    text: string;
    counterText: string;
    placeholder: string;
    buttonText: string;
    maxValues?: number;
};

const BulkAddModal = ({
    onAddValues,
    onOpen,
    onClose,
    header,
    text,
    counterText,
    placeholder,
    buttonText,
    parseInputValue,
    maxValues = 500,
}: BulkAddModalProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rawValue, setRawValue] = useState("");
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const parsedRawValue = useMemo(() => parseInputValue(rawValue), [parseInputValue, rawValue]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
        onOpen?.();
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setRawValue("");
        onClose?.();
    };

    const handleAdd = () => {
        onAddValues(parsedRawValue);
        handleModalClose();
    };

    const onFocus = () => {
        setIsFocused(true);
    };

    const onBlur = () => {
        setIsFocused(false);
    };

    return (
        <>
            <Button
                type="flat"
                onClick={handleOpenModal}
            >
                {buttonText}
            </Button>

            <CustomBasicModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                withCloseIcon={false}
                customStyles={modalStyles}
            >
                <StyledHeader>{header}</StyledHeader>
                <StyledText>{text}</StyledText>
                <StyledPositionRelative>
                    <StyledTextareaContainer focus={isFocused}>
                        <StyledTextarea
                            autoFocus
                            placeholder={placeholder}
                            onChange={(event) => {
                                const value = event.target.value;
                                if (parseInputValue(value).length <= maxValues) {
                                    setRawValue(value);
                                }
                            }}
                            value={rawValue}
                            onFocus={onFocus}
                            onBlur={onBlur}
                        />
                    </StyledTextareaContainer>
                    <StyledCounter isFull={parsedRawValue.length === maxValues}>
                        {parsedRawValue.length}/{maxValues} {counterText}
                    </StyledCounter>
                </StyledPositionRelative>
                <StyledButtonsContainer>
                    <Button
                        type="flat"
                        onClick={handleModalClose}
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Button
                        onClick={handleAdd}
                        isDisabled={rawValue === ""}
                    >
                        {translate("si.common.button.add")}
                    </Button>
                </StyledButtonsContainer>
            </CustomBasicModal>
        </>
    );
};

export default BulkAddModal;
